import { useState } from "react";
import API from "@src/API.js";
import CustomButton from "./CustomButton";
import { toast } from "react-toastify";

/**
 * Blocking full-screen gate after login when MustChangePassword is set.
 * Not dismissible — user must set a new password to continue.
 */
export default function MustChangePasswordGate({ onComplete }) {
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e?.preventDefault?.();
    if (busy) return;
    if (!password || !passwordConfirm) {
      setError("Enter and confirm your new password.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const resp = await API.postAsync("/Person/SetPasswordRequired", {
        password,
        passwordConfirm,
      });
      if (resp?.ok) {
        window.sessionStorage.removeItem("MustChangePassword");
        toast.success("Password updated");
        onComplete?.();
      } else {
        setError(resp?.jsonData?.message || "Could not update password");
      }
    } catch (err) {
      setError(err?.message || "Could not update password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="cm-modal-overlay is-entering"
      data-testid="must-change-password-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="must-change-password-title"
      style={{ zIndex: 10000 }}
    >
      <div className="cm-modal-dialog">
        <div className="cm-modal-dialog__header">
          <h2 id="must-change-password-title">Password change required</h2>
        </div>
        <div className="cm-modal-dialog__body">
          <div className="cm-modal-embedded">
            <div className="cm-modal-body-pad">
              <p className="fieldHint">
                Your account requires a new password before you can continue. You do not need your
                old password.
              </p>
              <form className="changePasswordForm" onSubmit={onSubmit}>
                <div className="field form-group">
                  <label className="form-label" htmlFor="forced-password">
                    New password
                  </label>
                  <input
                    id="forced-password"
                    type="password"
                    className="form-input"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="field form-group">
                  <label className="form-label" htmlFor="forced-password-confirm">
                    Confirm new password
                  </label>
                  <input
                    id="forced-password-confirm"
                    type="password"
                    className="form-input"
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    required
                  />
                </div>
                {error ? (
                  <p className="fieldHint" style={{ color: "#b00020" }}>
                    {error}
                  </p>
                ) : null}
              </form>
            </div>
          </div>
        </div>
        <div className="cm-modal-dialog__footer form-actions">
          <CustomButton
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={busy}
            data-testid="must-change-password-submit"
          >
            Set password
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
